import type { ThreadTaskSuggestion } from '@frontend/api-types';
import { Fragment } from '@frontend/react';
import { Clickable, Divider, Icon, LinearGradientWrapper, Skeleton, Text, XStack, YStack } from '@frontend/web-react';
import { concatClasses } from '@frontend/web-utils';

export type ThreadSuggestionCard = {
  isAnyStreaming: boolean;
  isCurrentStreaming: boolean;
  isStandalone?: boolean;
  isThrottled: boolean;
  onClick: () => void;
  suggestion: ThreadTaskSuggestion;
};

export const ThreadSuggestionCard = ({
  isAnyStreaming,
  isCurrentStreaming,
  isStandalone,
  isThrottled,
  onClick,
  suggestion: { isRecurrence, recurrenceId, taskId, title },
}: ThreadSuggestionCard) => {
  const wasUsed = !!taskId || !!recurrenceId;
  const isLoading = isCurrentStreaming || isAnyStreaming;

  if (isStandalone) {
    return (
      <Clickable
        className={concatClasses(
          // eslint-disable-next-line tailwindcss/no-arbitrary-value
          'bg-white rounded-md border overflow-hidden w-[330px]',
          isLoading ? 'border-none' : 'border-borderPrimary',
        )}
        disabled={isLoading}
        enableHoverEffect
        onClick={onClick}
      >
        {isLoading ? (
          <YStack>
            {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
            <XStack className="py-sm" />
            <Divider />
            <XStack className="gap-x-sm p-md">
              <Skeleton height={24} radius={6} width={266} />
              <Skeleton height={24} radius={6} width={24} />
            </XStack>
          </YStack>
        ) : (
          <YStack>
            <LinearGradientWrapper variant="blue">
              <YStack className="items-center justify-center p-1">
                <Text className="italic" color={'accent'} type="captionBold" uppercase>
                  {wasUsed ? 'Go to task' : isThrottled && !isRecurrence ? 'Create Draft' : 'Send to copilot'}
                </Text>
              </YStack>
            </LinearGradientWrapper>
            <XStack className="gap-x-sm p-md items-center justify-between">
              <Text
                className={wasUsed ? 'line-through' : 'no-underline'}
                color={wasUsed ? 'secondary' : 'primary'}
                lineClamp={2}
              >
                {title}
              </Text>
              <Icon name="IconChevronRight" />
            </XStack>
          </YStack>
        )}
      </Clickable>
    );
  }

  return (
    <Clickable
      // eslint-disable-next-line tailwindcss/no-arbitrary-value
      className={'border-borderPrimary w-[200px] rounded-md border bg-white'}
      disabled={isLoading}
      enableHoverEffect
      onClick={onClick}
    >
      <YStack className="p-sm flex-1">
        {isLoading ? (
          <YStack className="gap-sm">
            <Skeleton height={14} radius={6} width={140} />
            <Skeleton height={14} radius={6} width={60} />
          </YStack>
        ) : (
          <Text
            className={wasUsed ? 'line-through' : 'no-underline'}
            color={wasUsed ? 'tertiary' : 'primary'}
            type="p-sm"
          >
            {title}
          </Text>
        )}
      </YStack>
      <Divider />
      <XStack className="gap-xs p-sm items-center">
        {isLoading ? (
          <Skeleton height={14} radius={6} width={100} />
        ) : (
          <Fragment>
            <Text type="captionBold">
              {wasUsed ? 'Go to task' : isThrottled && !isRecurrence ? 'Create Draft' : 'Send to copilot'}
            </Text>
            <Icon color="primary" name="IconArrowRight" size={18} />
          </Fragment>
        )}
      </XStack>
    </Clickable>
  );
};
